class CustomSizeGuideDrawer extends HTMLElement {
  static STORAGE_KEY = 'faffies:size-guide:measurements:v1';

  connectedCallback() {
    if (this.abortController) return;

    // Teleport to <body> so the fixed panel escapes the sticky product-column
    // stacking context (.product__column-sticky has z-index 2) and can overlay
    // the site header like the cart drawer does. The trigger button stays put.
    if (!this.teleported) {
      this.optionFieldset = this.closest('fieldset');
      if (!this.openButton) this.openButton = this.optionFieldset?.querySelector('[data-custom-action="open-size-guide"]');
      this.teleported = true;
      document.body.appendChild(this);
      // Remove orphaned copies from theme-editor section re-renders.
      const sectionId = this.dataset.sectionId;
      const optionPosition = this.dataset.optionPosition;
      if (sectionId && optionPosition) {
        document
          .querySelectorAll(`body > custom-size-guide-drawer[data-section-id="${sectionId}"][data-option-position="${optionPosition}"]`)
          .forEach((node) => {
            if (node !== this) node.remove();
          });
      }
      return; // init runs on the re-connect fired by appendChild
    }

    this.abortController = new AbortController();
    // The data file loads in a deferred script tag ahead of this one, but if a
    // cached/partial fetch won the race, retry on window load instead of
    // initialising with no data (which would wrongly drop the switcher).
    if (!window.FaffiesSizeGuides) {
      window.addEventListener('load', () => this.connectedCallback(), { once: true });
      this.abortController = null;
      return;
    }
    this.closeButton = this.querySelector('[data-custom-action="close-size-guide"]');
    this.panel = this.querySelector('[data-custom-target="size-guide-panel"]');

    if (!this.openButton || !this.closeButton || !this.panel) return;

    this.sectionId = this.dataset.sectionId || '';
    this.fitCategory = this.dataset.fitCategory || 'mens-swim-briefs';
    this.activeTab = 'chart';
    this.unit = this.readSavedState().unit === 'in' ? 'in' : 'cm';
    this.lastMatch = null;

    this.tabButtons = [...this.querySelectorAll('[data-custom-tab]')];
    this.panels = [...this.querySelectorAll('[data-custom-panel]')];
    this.fitButtonsWrap = this.querySelector('[data-fit-buttons]');
    this.fitButtons = [];
    this.unitButtons = [...this.querySelectorAll('[data-unit]')];
    this.chartTitle = this.querySelector('[data-chart-title]');
    this.chartScrollWrap = this.querySelector('[data-chart-scroll]');
    this.chartHeaderRow = this.querySelector('[data-chart-header-row]');
    this.chartBody = this.querySelector('[data-chart-body]');
    this.howtoHeading = this.querySelector('[data-howto-heading]');
    this.howtoBody = this.querySelector('[data-howto-body]');
    this.howtoNote = this.querySelector('[data-howto-note]');
    this.howtoImage = this.querySelector('[data-howto-image]');
    this.calcHelp = this.querySelector('[data-calc-help]');
    this.calcLabel = this.querySelector('[data-calc-label]');
    this.calcInput = this.querySelector('[data-calc-input]');
    this.calcSuffix = this.querySelector('[data-calc-suffix]');
    this.calcError = this.querySelector('[data-calc-error]');
    this.calcResult = this.querySelector('[data-calc-result]');
    this.calcResultSize = this.querySelector('[data-calc-result-size]');
    this.calcResultMessage = this.querySelector('[data-calc-result-message]');
    this.calcApply = this.querySelector('[data-calc-apply]');

    const listenerOptions = { signal: this.abortController.signal };
    this.openButton.addEventListener('click', () => this.show(), listenerOptions);
    this.closeButton.addEventListener('click', () => this.hide(), listenerOptions);
    this.panel.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' || event.code.toUpperCase() === 'ESCAPE') this.hide();
      },
      listenerOptions
    );

    this.tabButtons.forEach((button) => {
      button.addEventListener('click', () => this.setTab(button.dataset.customTab), listenerOptions);
      button.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const index = this.tabButtons.indexOf(button);
          const next =
            event.key === 'ArrowRight'
              ? this.tabButtons[(index + 1) % this.tabButtons.length]
              : this.tabButtons[(index - 1 + this.tabButtons.length) % this.tabButtons.length];
          next.focus();
          this.setTab(next.dataset.customTab);
        },
        listenerOptions
      );
    });

    this.buildFitButtons(listenerOptions);

    this.unitButtons.forEach((button) => {
      button.addEventListener('click', () => this.setUnit(button.dataset.unit), listenerOptions);
    });

    this.chartScrollWrap?.addEventListener('wheel', (event) => this.scrollChartWithWheel(event), {
      ...listenerOptions,
      passive: false
    });

    this.querySelector('[data-calc-submit]')?.addEventListener('click', () => this.runCalculator(), listenerOptions);
    this.calcInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.runCalculator();
      }
    }, listenerOptions);
    this.calcInput?.addEventListener('input', () => {
      this.clearResult(false);
      this.rememberCurrentInput();
    }, listenerOptions);
    this.calcApply?.addEventListener('click', () => this.applySize(), listenerOptions);

    this.renderAll();
    const restoredCm = this.restoreSavedMeasurement();
    if (restoredCm !== null) this.runCalculator({ reveal: false, valueCm: restoredCm });
  }

  disconnectedCallback() {
    this.hide(false);
    this.abortController?.abort();
    this.abortController = null;
  }

  handleBodyClick = (event) => {
    // contains(), not ===: a click on the button's icon targets the <svg>/<path>,
    // and that same click bubbles here right after show() adds this listener.
    if (!this.panel.contains(event.target) && !this.openButton?.contains(event.target)) this.hide();
  };

  show() {
    this.panel.setAttribute('open', '');
    this.panel.scrollTop = 0;
    document.body.classList.add('overflow-hidden');
    document.body.addEventListener('click', this.handleBodyClick);
    trapFocus(this.panel);
  }

  hide(restoreFocus = true) {
    if (!this.panel?.hasAttribute('open')) return;

    this.panel.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    document.body.removeEventListener('click', this.handleBodyClick);
    removeTrapFocus(restoreFocus ? this.openButton : null);
  }

  guide() {
    const guides = window.FaffiesSizeGuides || {};
    return guides[this.fitCategory] || guides['mens-swim-briefs'];
  }

  readSavedState() {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(CustomSizeGuideDrawer.STORAGE_KEY));
      if (saved && typeof saved === 'object' && saved.measurements && typeof saved.measurements === 'object') {
        return saved;
      }
    } catch (_) {
      // Storage can be disabled; the guide still works for the current page.
    }
    return { unit: 'cm', measurements: {} };
  }

  writeSavedState(state) {
    try {
      window.sessionStorage.setItem(CustomSizeGuideDrawer.STORAGE_KEY, JSON.stringify(state));
    } catch (_) {
      // Storage is optional; a failed write must not block recommendations.
    }
  }

  measurementKey() {
    const guide = this.guide();
    return `${guide.audience || 'adult'}:${guide.measure}`;
  }

  restoreSavedMeasurement() {
    if (!this.calcInput) return null;
    const valueCm = this.readSavedState().measurements[this.measurementKey()];
    this.calcInput.value = Number.isFinite(valueCm) && valueCm > 0 ? String(this.cmToDisplay(valueCm)) : '';
    return this.calcInput.value ? valueCm : null;
  }

  rememberCurrentInput() {
    if (!this.calcInput) return;
    const raw = Number.parseFloat(this.calcInput.value);
    const valueCm = this.unit === 'in' ? raw * 2.54 : raw;
    const saved = this.readSavedState();
    if (Number.isFinite(valueCm) && valueCm >= 20 && valueCm <= 220) {
      saved.measurements[this.measurementKey()] = valueCm;
    } else {
      delete saved.measurements[this.measurementKey()];
    }
    this.writeSavedState(saved);
  }

  setTab(name) {
    this.activeTab = name;
    this.tabButtons.forEach((button) => {
      const selected = button.dataset.customTab === name;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) button.removeAttribute('tabindex');
      else button.setAttribute('tabindex', '-1');
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.customPanel !== name;
    });
  }

  setFit(fit) {
    const guides = window.FaffiesSizeGuides || {};
    if (fit && !guides[fit]) fit = this.fitCategories[0] || 'mens-swim-briefs';
    if (!fit || fit === this.fitCategory) return;
    this.fitCategory = fit;
    this.lastMatch = null;
    this.syncFitButtons();
    this.clearResult(false);
    this.renderAll();
    const restoredCm = this.restoreSavedMeasurement();
    if (restoredCm !== null) this.runCalculator({ reveal: false, valueCm: restoredCm });
  }

  buildFitButtons(listenerOptions) {
    const guides = window.FaffiesSizeGuides || {};
    const listed = (this.dataset.fitCategories || '')
      .split(',')
      .map((key) => key.trim())
      .filter((key) => guides[key]);
    this.fitCategories = listed.length ? listed : [this.fitCategory];
    if (!guides[this.fitCategory]) this.fitCategory = this.fitCategories[0] || 'mens-swim-briefs';

    const switcher = this.fitButtonsWrap?.closest('[data-fit-switcher]');
    if (!this.fitButtonsWrap || this.fitCategories.length < 2) {
      switcher?.remove();
      this.fitButtonsWrap = null;
      this.fitButtons = [];
      return;
    }
    this.fitButtons = this.fitCategories.map((key) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.fit = key;
      button.textContent = guides[key].label;
      button.setAttribute('aria-pressed', key === this.fitCategory ? 'true' : 'false');
      button.addEventListener('click', () => this.setFit(key), listenerOptions);
      this.fitButtonsWrap.append(button);
      return button;
    });
  }

  syncFitButtons() {
    (this.fitButtons || []).forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.fit === this.fitCategory ? 'true' : 'false');
    });
  }

  setUnit(unit) {
    if (unit !== 'cm' && unit !== 'in') return;
    const current = Number.parseFloat(this.calcInput?.value || '');
    const valueCm = Number.isFinite(current) ? (this.unit === 'in' ? current * 2.54 : current) : null;
    this.unit = unit;
    const saved = this.readSavedState();
    saved.unit = unit;
    this.writeSavedState(saved);
    if (this.calcInput && valueCm !== null) this.calcInput.value = String(this.cmToDisplay(valueCm));
    this.unitButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.unit === unit ? 'true' : 'false');
    });
    this.renderAll();
    if (this.calcInput?.value) this.runCalculator({ reveal: false, valueCm });
  }

  cmToDisplay(cm) {
    if (this.unit === 'in') return Math.round((cm / 2.54) * 10) / 10;
    return Math.round(cm * 10) / 10;
  }

  scrollChartWithWheel(event) {
    const wrap = this.chartScrollWrap;
    if (!wrap || wrap.scrollWidth <= wrap.clientWidth + 1 || event.ctrlKey || event.metaKey || event.shiftKey) return;
    // Native horizontal wheel/trackpad movement already scrolls the table.
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY) || !event.deltaY) return;
    const factor = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? wrap.clientWidth : 1;
    const maxScroll = wrap.scrollWidth - wrap.clientWidth;
    const next = Math.max(0, Math.min(maxScroll, wrap.scrollLeft + event.deltaY * factor));
    if (Math.abs(next - wrap.scrollLeft) < 1) return;
    wrap.scrollLeft = next;
    event.preventDefault();
  }

  formatRange(row) {
    const min = this.cmToDisplay(row.minCm);
    const max = this.cmToDisplay(row.maxCm);
    return `${min}–${max}`;
  }

  renderAll() {
    this.unitButtons.forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.unit === this.unit ? 'true' : 'false');
    });
    this.renderChart();
    this.renderCalcLabels();
  }

  renderChart() {
    const guide = this.guide();
    if (!guide) return;
    const unitLabel = this.unit === 'in' ? 'in' : 'cm';
    if (this.chartTitle) this.chartTitle.textContent = guide.title;
    const columns = guide.chartColumns || [{ key: 'range', label: guide.measure === 'chest' ? 'Chest (body)' : 'Waist (body)', format: 'range' }];
    if (this.chartHeaderRow) {
      this.chartHeaderRow.replaceChildren();
      ['Size', ...columns.map((column) => `${column.label} (${unitLabel})`)].forEach((label) => {
        const heading = document.createElement('th');
        heading.scope = 'col';
        heading.textContent = label;
        this.chartHeaderRow.append(heading);
      });
    }
    if (this.howtoHeading) this.howtoHeading.textContent = guide.howToHeading;
    if (this.howtoBody) this.howtoBody.textContent = guide.howToBody;
    if (this.howtoNote) this.howtoNote.textContent = guide.howToNote;
    const howToImage = guide.howToImageAsset ? this.dataset[guide.howToImageAsset] : guide.howToImage;
    if (this.howtoImage && howToImage) {
      this.howtoImage.src = howToImage;
      this.howtoImage.alt = guide.howToImageAlt || guide.howToHeading;
      const isChest = guide.measure === 'chest';
      this.howtoImage.classList.toggle('is-chest', isChest);
      this.howtoImage.classList.toggle('is-waist', !isChest);
    }
    if (!this.chartBody) return;

    this.chartBody.innerHTML = '';
    (guide.chartRows || guide.ranges).forEach((row) => {
      const tr = document.createElement('tr');
      if (this.lastMatch && this.lastMatch.size === row.size) tr.classList.add('is-match');
      const sizeCell = document.createElement('td');
      sizeCell.textContent = row.size;
      tr.append(sizeCell);
      columns.forEach((column) => {
        const valueCell = document.createElement('td');
        const value = row[column.key];
        if (column.format === 'range') valueCell.textContent = this.formatRange(row);
        else if (Number.isFinite(value)) valueCell.textContent = String(this.cmToDisplay(value));
        else {
          valueCell.textContent = '—';
          valueCell.setAttribute('aria-label', 'Measurement not available');
          valueCell.title = 'Measurement not provided';
        }
        tr.append(valueCell);
      });
      this.chartBody.append(tr);
    });
  }

  renderCalcLabels() {
    const guide = this.guide();
    if (!guide) return;
    if (this.calcHelp) this.calcHelp.textContent = guide.measureHelp;
    if (this.calcLabel) {
      const label = this.calcLabel;
      const inputId = this.calcInput?.id || '';
      label.setAttribute('for', inputId);
      label.textContent = guide.measureLabel;
    }
    if (this.calcSuffix) this.calcSuffix.textContent = this.unit === 'in' ? 'in' : 'cm';
    if (this.calcInput) {
      const maxHint = this.unit === 'in' ? 'e.g. 34' : 'e.g. 86';
      this.calcInput.setAttribute('placeholder', `Enter measurement (${maxHint})`);
    }
  }

  clearResult(hideError = true) {
    this.lastMatch = null;
    if (this.calcResult) this.calcResult.hidden = true;
    if (this.calcApply) this.calcApply.hidden = true;
    if (hideError && this.calcError) this.calcError.hidden = true;
    this.querySelectorAll('[data-chart-body] tr.is-match').forEach((row) => row.classList.remove('is-match'));
  }

  showError(message) {
    if (!this.calcError) return;
    this.calcError.textContent = message;
    this.calcError.hidden = false;
  }

  runCalculator({ reveal = true, valueCm: knownCm = null } = {}) {
    const guide = this.guide();
    if (!guide || !this.calcInput) return;
    this.clearResult();
    this.renderChart();

    const rawText = this.calcInput.value.trim();
    if (rawText === '') return; // empty input: do nothing, no error
    const raw = Number.parseFloat(rawText);
    if (!Number.isFinite(raw) || raw <= 0) {
      this.showError('Please enter your measurement to get started.');
      return;
    }
    const valueCm = Number.isFinite(knownCm) ? knownCm : (this.unit === 'in' ? raw * 2.54 : raw);
    if (valueCm < 20 || valueCm > 220) {
      this.showError(this.unit === 'in' ? 'That looks off — please check inches and try again.' : 'That looks off — please check cm and try again.');
      return;
    }

    const saved = this.readSavedState();
    saved.measurements[this.measurementKey()] = valueCm;
    this.writeSavedState(saved);

    const ranges = guide.ranges;
    let exact = ranges.find((row) => valueCm >= row.minCm && valueCm <= row.maxCm);
    // Shared boundaries belong to both rows; prefer the smaller size on exact ties.
    if (!exact) {
      const belowOrEqual = ranges.filter((row) => valueCm <= row.maxCm);
      exact = belowOrEqual.length ? belowOrEqual[0] : null;
    }

    if (exact) {
      const isBoundary = ranges.some(
        (row) => row !== exact && Math.abs(valueCm - row.minCm) < 0.001 && Math.abs(valueCm - row.maxCm) < 0.001
      );
      this.lastMatch = exact;
      this.renderChart();
      this.showResult(exact.size, isBoundary ? 'Right on the boundary — size up for comfort.' : `Based on your ${guide.measure} measurement.`, true, reveal);
      return;
    }

    if (valueCm < ranges[0].minCm) {
      this.showResult(ranges[0].size, 'Below our smallest size — try the smallest size or contact us for help.', false);
      return;
    }
    const largest = ranges[ranges.length - 1];
    if (valueCm > largest.maxCm) {
      this.showResult(largest.size, 'Above our largest size — try the largest size or contact us for help.', false);
    }
  }

  showResult(size, message, selectable = true, reveal = true) {
    if (this.calcResultSize) this.calcResultSize.textContent = `Size ${size}`;
    if (this.calcResultMessage) this.calcResultMessage.textContent = message;
    if (this.calcResult) this.calcResult.hidden = false;
    if (this.calcApply) this.calcApply.hidden = !selectable || !this.findVariantInput(size);
    if (selectable && reveal) this.setTab('calculator');
  }

  sizePickerRoot() {
    if (this.sectionId && this.fitCategory !== this.dataset.fitCategory) {
      const peer = [...document.querySelectorAll('body > custom-size-guide-drawer')].find(
        (drawer) => drawer !== this &&
          drawer.dataset.sectionId === this.sectionId &&
          drawer.dataset.fitCategory === this.fitCategory &&
          drawer.optionFieldset?.isConnected
      );
      if (peer) return peer.optionFieldset;
    }
    if (this.optionFieldset) return this.optionFieldset.isConnected ? this.optionFieldset : null;
    if (this.sectionId) {
      const scoped = document.getElementById(`variant-selects-${this.sectionId}`);
      if (scoped) return scoped;
    }
    return this.closest('variant-selects') || document;
  }

  findVariantInput(size) {
    const root = this.sizePickerRoot();
    if (!root) return null;
    const wanted = String(size).trim().toLowerCase();
    const inputs = [...root.querySelectorAll('input[type="radio"]')];
    return (
      inputs.find((input) => String(input.value || '').trim().toLowerCase() === wanted && !input.disabled) || null
    );
  }

  applySize() {
    if (!this.lastMatch) return;
    const input = this.findVariantInput(this.lastMatch.size);
    if (!input) return;
    input.click();
    this.hide();
  }
}

if (!customElements.get('custom-size-guide-drawer')) {
  customElements.define('custom-size-guide-drawer', CustomSizeGuideDrawer);

  // Links elsewhere on the page (FAQ answers, rich text) open the drawer too:
  // any link ending in #size-guide, such as /pages/sizing-chart#size-guide,
  // which still lands on the chart page wherever there's no drawer.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href$="#size-guide"]');
    const drawer = link && document.querySelector('body > custom-size-guide-drawer');
    if (!drawer?.panel) return;
    event.preventDefault();
    drawer.show();
  });
}

/* ==========================================================================
   Quick view
   A single shared overlay: centred popup on desktop, bottom sheet on mobile.
   Content comes from the `product.custom-quickview` template via the Section
   Rendering API, so merchants compose it in the theme editor, not in code.
   ========================================================================== */

/**
 * Section ids for JSON-template sections are fully qualified at runtime
 * (`template--<templateId>__<key>`), and that id encodes the template, so
 * `?section_id=<qualified id>` resolves the quick view template on its own —
 * no `view` param and no patching of product-info.js required. The id is not
 * knowable from Liquid at card-render time, so it is discovered once per
 * session (see fetchSection) and cached.
 */
class CustomQuickview extends HTMLElement {
  static DRAG_DISMISS_RATIO = 0.28;
  static DRAG_DISMISS_VELOCITY = 0.5; // px/ms
  static OPENER_SELECTOR = '[data-custom-quickview-url]';
  static SECTION_ID_CACHE_KEY = 'custom-quickview:section-id';
  /* Safety net for the short-description text block. Merchants author to this
     budget in the metafield; anything longer is trimmed rather than allowed to
     push the buy button out of view. */
  static SHORT_DESCRIPTION_LIMIT = 200;
  /* Gallery slides kept for the overlay carousel. */
  static MAX_MEDIA = 8;

  static get instance() {
    return document.querySelector('custom-quickview');
  }

  /**
   * One delegated listener covers every card on the page, including cards
   * rendered later by collection filtering or product recommendations.
   */
  static bindDelegatedOpeners() {
    if (CustomQuickview.openersBound) return;
    CustomQuickview.openersBound = true;

    document.addEventListener('click', (event) => {
      const opener = event.target.closest(CustomQuickview.OPENER_SELECTOR);
      if (!opener) return;

      // Let cmd/ctrl/shift/middle clicks open the product page in a new tab.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      const quickview = CustomQuickview.instance;
      const productUrl = opener.dataset.customQuickviewUrl;
      if (!quickview || !productUrl) return; // no component: the <a href> navigates

      event.preventDefault();
      quickview.show(productUrl, opener);
    });
  }

  connectedCallback() {
    if (this.abortController) return;

    // Teleport to <body> so the fixed panel escapes any transformed or
    // stacking-context ancestor, matching the size guide drawer.
    if (!this.teleported) {
      this.teleported = true;
      document.body.appendChild(this);
      return; // init runs on the re-connect fired by appendChild
    }

    // Remove orphaned copies left by theme-editor section re-renders.
    document.querySelectorAll('custom-quickview').forEach((node) => {
      if (node !== this) node.remove();
    });

    this.abortController = new AbortController();
    this.panel = this.querySelector('[data-custom-target="quickview-panel"]');
    this.scrim = this.querySelector('[data-custom-target="quickview-scrim"]');
    this.scrollEl = this.querySelector('[data-custom-target="quickview-scroll"]');
    this.contentEl = this.querySelector('[data-custom-target="quickview-content"]');
    this.grabber = this.querySelector('[data-custom-target="quickview-grabber"]');
    this.statusEl = this.querySelector('[data-custom-target="quickview-status"]');
    this.navTemplate = this.querySelector('[data-custom-target="quickview-nav"]');

    if (!this.panel || !this.contentEl) return;

    this.view = this.dataset.view || 'custom-quickview';
    this.openedBy = null;
    this.pendingSubmit = false;

    const listenerOptions = { signal: this.abortController.signal };

    this.querySelectorAll('[data-custom-action="close-quickview"]').forEach((button) => {
      button.addEventListener('click', () => this.hide(), listenerOptions);
    });
    this.scrim?.addEventListener('click', () => this.hide(), listenerOptions);
    this.addEventListener(
      'keyup',
      (event) => {
        if (event.key === 'Escape' || event.code?.toUpperCase() === 'ESCAPE') this.hide();
      },
      listenerOptions
    );

    // product-form.js publishes cartUpdate for every add to cart on the page,
    // including the untouched bulk quick-add path. Flag submissions that
    // originate inside this overlay so unrelated adds cannot close it.
    this.contentEl.addEventListener('submit', () => { this.pendingSubmit = true; }, {
      capture: true,
      signal: this.abortController.signal,
    });

    // HTML arriving from a variant swap must be stripped and re-stamped too.
    this.addEventListener(
      'product-info:loaded',
      ({ target }) => target.addPreProcessCallback?.(this.preprocessHTML.bind(this)),
      listenerOptions
    );

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => {
      if (this.hasAttribute('open')) this.refreshJudgeMe();
    });

    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.handleBreakpointChange = () => {
      if (this.hasAttribute('open')) this.bindDragHandlers();
    };
    this.desktopQuery.addEventListener('change', this.handleBreakpointChange);

    CustomQuickview.bindDelegatedOpeners();
    this.prefetchSectionId();
  }

  /**
   * Discovering the quick view template's section id costs one full-page render.
   * Doing it lazily made the first open of a session visibly slow, so warm the
   * cache while the browser is idle and the shopper is still browsing.
   */
  prefetchSectionId() {
    if (this.cachedSectionId) return;

    const schedule = window.requestIdleCallback || ((callback) => setTimeout(callback, 1200));
    schedule(() => {
      const productUrl = document.querySelector(CustomQuickview.OPENER_SELECTOR)?.dataset.customQuickviewUrl;
      if (!productUrl || this.cachedSectionId) return;

      fetch(`${productUrl.split('?')[0]}?view=${encodeURIComponent(this.view)}`)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          this.cachedSectionId = html.querySelector('product-info')?.dataset.section || null;
        })
        .catch(() => {
          /* the first open will discover it instead */
        });
    });
  }

  disconnectedCallback() {
    this.fetchAbortController?.abort();
    this.abortController?.abort();
    this.abortController = null;
    this.unbindDragHandlers();
    this.desktopQuery?.removeEventListener('change', this.handleBreakpointChange);
    this.cartUpdateUnsubscriber?.();
    this.variantChangeUnsubscriber?.();
    clearTimeout(this.clearContentTimer);
    document.body.classList.remove('overflow-hidden');
  }

  /* ---------------------------------------------------------------- open */

  async show(productUrl, opener) {
    if (this.hasAttribute('open')) return;

    this.openedBy = opener || null;
    this.openedBy?.setAttribute('aria-expanded', 'true');
    this.setOpenerBusy(true);

    // Open first, load second: the sheet animates in immediately behind a
    // skeleton rather than stalling on the network.
    clearTimeout(this.clearContentTimer);
    this.setAttribute('data-loading', '');
    this.panel.setAttribute('aria-busy', 'true');
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    if (this.scrollEl) this.scrollEl.scrollTop = 0;
    pauseAllMedia();
    trapFocus(this, this.panel);
    this.bindDragHandlers();

    try {
      const html = await this.fetchSection(productUrl);
      const productElement = html.querySelector('product-info');
      if (!productElement) throw new Error('custom-quickview: no product-info in response');
      this.render(productElement);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      window.location.href = productUrl; // never a dead end
      return;
    } finally {
      this.setOpenerBusy(false);
      this.removeAttribute('data-loading');
      this.panel.removeAttribute('aria-busy');
    }
  }

  get cachedSectionId() {
    try {
      return window.sessionStorage.getItem(CustomQuickview.SECTION_ID_CACHE_KEY);
    } catch (error) {
      return null;
    }
  }

  set cachedSectionId(value) {
    try {
      if (value) window.sessionStorage.setItem(CustomQuickview.SECTION_ID_CACHE_KEY, value);
      else window.sessionStorage.removeItem(CustomQuickview.SECTION_ID_CACHE_KEY);
    } catch (error) {
      /* private browsing — discovery just runs every time */
    }
  }

  /**
   * Once the quick view template's section id is known, each open costs a small
   * section fragment. The first open per session pays for a full-page render of
   * the alternate template to discover that id — the same cost as the stock
   * Dawn quick add, which fetches the whole product page every single time.
   */
  async fetchSection(productUrl, optionValueIds = []) {
    this.fetchAbortController?.abort();
    this.fetchAbortController = new AbortController();
    const { signal } = this.fetchAbortController;

    // Strip any ?variant= already on the card link (mirrors BulkModal).
    const baseUrl = productUrl.split('?')[0];
    const cachedSectionId = this.cachedSectionId;
    // Preselects the matching variant, e.g. the size carried over by a colour swatch.
    const optionValues = optionValueIds.length ? `&option_values=${optionValueIds.join(',')}` : '';

    if (cachedSectionId) {
      // `view` has to travel with `section_id`. Both product templates name the
      // section "main", so `?section_id=main` alone resolves against the default
      // product template and quietly returns the full PDP configuration instead
      // of the quick view one — same element, wrong blocks. It still contains
      // <product-info>, so the staleness check below never catches it.
      const response = await fetch(
        `${baseUrl}?section_id=${encodeURIComponent(cachedSectionId)}&view=${encodeURIComponent(this.view)}${optionValues}`,
        { signal }
      );
      if (response.ok) {
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (html.querySelector('product-info')) return html;
      }
      // Stale id (the theme was re-uploaded): fall through and rediscover.
      this.cachedSectionId = null;
    }

    const response = await fetch(`${baseUrl}?view=${encodeURIComponent(this.view)}${optionValues}`, { signal });
    if (!response.ok) throw new Error(`custom-quickview: ${response.status} for ${baseUrl}`);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    this.cachedSectionId = html.querySelector('product-info')?.dataset.section || null;
    return html;
  }

  /**
   * Replaces the open overlay's product with a sibling colour, picked from the
   * colour swatches inside it. The sheet stays open and keeps its scroll
   * position; only the content behind the swatches is swapped.
   */
  async swapProduct(productUrl, optionValueIds = []) {
    if (!this.hasAttribute('open')) return;

    this.panel.setAttribute('aria-busy', 'true');
    try {
      const html = await this.fetchSection(productUrl, optionValueIds);
      const productElement = html.querySelector('product-info');
      if (!productElement) throw new Error('custom-quickview: no product-info in response');

      const scrollTop = this.scrollEl?.scrollTop || 0;
      this.render(productElement);
      if (this.scrollEl) this.scrollEl.scrollTop = scrollTop;

      // The clicked swatch was replaced along with everything else; hand focus
      // to its fresh copy so it doesn't fall out of the trapped panel.
      this.contentEl
        .querySelector('[data-custom-colour-swatch][aria-current="true"]')
        ?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      window.location.href = productUrl; // never a dead end
    } finally {
      this.panel.removeAttribute('aria-busy');
    }
  }

  render(productElement) {
    this.preprocessHTML(productElement);

    // setInnerHTML (not innerHTML) re-creates <script> tags so the injected
    // custom elements upgrade.
    HTMLUpdateUtility.setInnerHTML(this.contentEl, productElement.outerHTML);

    this.afterRender();
  }

  afterRender() {
    if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
    if (window.ProductModel) window.ProductModel.loadShopifyXR();
    this.refreshJudgeMe();

    // Re-trap so newly injected focusables enter the ring. trapFocus calls
    // removeTrapFocus() first, so calling it twice is safe.
    trapFocus(this, this.panel);

    const title = this.contentEl.querySelector('.product__title')?.textContent.trim();
    if (this.statusEl && title) this.statusEl.textContent = title;

    this.buildMediaCarousel();
  }

  /* ----------------------------------------------------------- carousel */

  /**
   * The gallery is a plain scroll-snap track (see the stylesheet) — Dawn's
   * SliderComponent sizes its slides against the viewport via .grid--peek, which
   * does not hold inside a narrower overlay. Here we only add the controls and
   * keep them in sync with the native scroll position, so swipe, trackpad and
   * keyboard all keep working on their own.
   */
  buildMediaCarousel() {
    this.syncMediaNav = null;

    const track = this.contentEl.querySelector('.product__media-list');
    if (!track) return;

    const slides = Array.from(track.querySelectorAll('.product__media-item'));
    track.toggleAttribute('data-carousel', slides.length > 1);
    if (slides.length < 2) return;

    const nav = this.navTemplate?.content.firstElementChild.cloneNode(true);
    if (!nav) return;

    const dotsEl = nav.querySelector('[data-custom-target="quickview-dots"]');
    const prev = nav.querySelector('[data-custom-action="media-prev"]');
    const next = nav.querySelector('[data-custom-action="media-next"]');

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} / ${slides.length}`);

      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'custom-quickview__dot';
      dot.setAttribute('aria-label', `${index + 1} ${dotsEl.dataset.ofLabel || 'of'} ${slides.length}`);
      dot.addEventListener('click', () => this.scrollToSlide(track, slides, index));
      dotsEl.appendChild(dot);
    });

    prev.addEventListener('click', () => this.stepSlide(track, slides, -1));
    next.addEventListener('click', () => this.stepSlide(track, slides, 1));

    track.parentElement.appendChild(nav);

    /**
     * `target` is passed by the dot/arrow handlers so the control lights up on
     * press rather than when the smooth scroll finishes; swipes leave it out and
     * the index is read back from the scroll position instead.
     */
    const sync = (target) => {
      const index = typeof target === 'number' ? target : this.activeSlideIndex(track, slides);
      Array.from(dotsEl.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      prev.disabled = index === 0;
      next.disabled = index === slides.length - 1;
    };

    this.syncMediaNav = sync;

    track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.mediaScrollFrame);
      this.mediaScrollFrame = requestAnimationFrame(() => sync());
    }, { passive: true });

    sync(0);
  }

  /**
   * Measured with getBoundingClientRect rather than offsetLeft: offsetLeft is
   * relative to the nearest positioned ancestor, which here is the panel, not
   * the scroll container — mixing that origin with scrollLeft gives a bogus
   * target and the track does not move.
   */
  activeSlideIndex(track, slides) {
    const middle = track.getBoundingClientRect().left + track.clientWidth / 2;
    let closest = 0;
    let smallest = Infinity;
    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - middle);
      if (distance < smallest) {
        smallest = distance;
        closest = index;
      }
    });
    return closest;
  }

  stepSlide(track, slides, direction) {
    const next = this.activeSlideIndex(track, slides) + direction;
    this.scrollToSlide(track, slides, Math.max(0, Math.min(slides.length - 1, next)));
  }

  scrollToSlide(track, slides, index) {
    this.syncMediaNav?.(index);

    const delta = slides[index].getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollTo({
      left: track.scrollLeft + delta,
      // 'instant', not 'auto': the track sets scroll-behavior:smooth in CSS, and
      // 'auto' defers to that — so reduced motion would still have animated.
      behavior: this.reducedMotionQuery?.matches ? 'instant' : 'smooth',
    });
  }

  /* ---------------------------------------------------------- preprocess */

  preprocessHTML(productElement) {
    this.preventDuplicatedIDs(productElement);
    this.removeDOMElements(productElement);
    this.removeGalleryListSemantic(productElement);
    this.limitMedia(productElement);
    this.truncateTextBlocks(productElement);
    this.lockVariantURL(productElement);
  }

  /** Rewrite section-scoped IDs so they cannot collide with the host page. */
  preventDuplicatedIDs(productElement) {
    const sectionId = productElement.dataset.section;
    if (!sectionId) return;

    const newId = `quickadd-${sectionId}`;
    productElement.innerHTML = productElement.innerHTML.replaceAll(sectionId, newId);
    Array.from(productElement.attributes).forEach((attribute) => {
      if (attribute.value.includes(sectionId)) {
        productElement.setAttribute(attribute.name, attribute.value.replace(sectionId, newId));
      }
    });

    productElement.dataset.originalSection = sectionId;
  }

  removeDOMElements(productElement) {
    const selectors = [
      'pickup-availability',
      'product-modal',
      'modal-dialog', // its constructor throws without an [id^="ModalClose-"] child
      'share-button',
      '.product__view-details',
      // The size guide drawer teleports itself to <body> on connect, so it would
      // leak an orphan on every open. Remove its trigger too, or the button is
      // left stranded; the <legend> label in the fieldset survives either way.
      'custom-size-guide-drawer',
      '[data-custom-action="open-size-guide"]',
    ];

    productElement.querySelectorAll(selectors.join(',')).forEach((node) => node.remove());
  }

  removeGalleryListSemantic(productElement) {
    const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
    if (!galleryList) return;

    galleryList.setAttribute('role', 'presentation');
    galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
  }

  /**
   * Cap the gallery rather than reducing it to the featured image: the overlay
   * carousel needs the other slides. The cap keeps the injected markup (and the
   * lazy image loads behind it) bounded for products with long galleries.
   */
  limitMedia(productElement) {
    productElement.querySelectorAll('.product__media-item').forEach((item, index) => {
      if (index >= CustomQuickview.MAX_MEDIA) item.remove();
    });
  }

  /** Browsing variants in the overlay must never rewrite the host page URL. */
  /**
   * Caps the short-description text block at a character budget. Walks text
   * nodes rather than slicing innerHTML so inline formatting (links, bold)
   * survives instead of being flattened or left with unclosed tags.
   */
  truncateTextBlocks(productElement) {
    const limit = CustomQuickview.SHORT_DESCRIPTION_LIMIT;

    productElement.querySelectorAll('.product__text').forEach((block) => {
      if (block.textContent.trim().length <= limit) return;

      const walker = block.ownerDocument.createTreeWalker(block, NodeFilter.SHOW_TEXT);
      const overflow = [];
      let used = 0;
      let node;

      while ((node = walker.nextNode())) {
        const remaining = limit - used;
        if (remaining <= 0) {
          overflow.push(node);
        } else if (node.data.length > remaining) {
          node.data = `${node.data.slice(0, remaining).trimEnd()}…`;
          used = limit;
        } else {
          used += node.data.length;
        }
      }

      overflow.forEach((textNode) => textNode.remove());
    });
  }

  lockVariantURL(productElement) {
    productElement.setAttribute('data-update-url', 'false');
  }

  /* --------------------------------------------------------------- cart */

  /* The cartUpdate payload is the published object itself (`event.source`),
     not a nested `{ data }` — see assets/cart.js:32. */
  onCartUpdate(event) {
    if (!this.hasAttribute('open')) return;
    if (event?.source !== 'product-form') return;
    if (!this.pendingSubmit) return; // an add to cart from elsewhere on the page

    this.pendingSubmit = false;

    // product-form.js publishes cartUpdate before it calls renderContents, so
    // we release focus before the cart drawer claims it.
    this.hide({ returnFocus: false });
    document.querySelector('cart-drawer')?.setActiveElement?.(this.openedBy);
  }

  /**
   * Judge.me's loader scans for `.jdgm-widget[data-entry-point]` exactly once, on
   * DOMContentLoaded, and there is no MutationObserver — so a widget injected
   * into the overlay afterwards is never mounted. Each widget's entry module
   * keeps its manager in module scope with no global handle, so the only way to
   * mount a late arrival is to re-evaluate that module under a fresh URL.
   *
   * On a collection page the module has never run at all, so its stylesheets are
   * missing too; those are resolved from Judge.me's own manifest, the same way
   * their loader does it.
   */
  async refreshJudgeMe() {
    const baseUrl = window.jdgm?.CDN_BASE_URL;
    const widgets = Array.from(this.contentEl.querySelectorAll('.jdgm-widget[data-entry-point]'));
    if (!baseUrl || !widgets.length) return;

    // A product page renders its own copy of these widgets, already mounted.
    // Hide them from the fresh scan so it cannot mount a second copy on top.
    const mountedElsewhere = Array.from(document.querySelectorAll('.jdgm-widget[data-entry-point]')).filter(
      (node) => !this.contentEl.contains(node)
    );
    mountedElsewhere.forEach((node) => {
      node.dataset.customQuickviewEntry = node.dataset.entryPoint;
      delete node.dataset.entryPoint;
    });

    const entryPoints = new Map(widgets.map((node) => [node.dataset.entryPoint, node.dataset.entryKey]));

    try {
      const manifest = await CustomQuickview.judgeMeManifest(baseUrl);
      await Promise.all(
        Array.from(entryPoints, async ([entryPoint, entryKey]) => {
          CustomQuickview.loadJudgeMeStyles(baseUrl, manifest, entryKey);
          await import(`${baseUrl}${entryPoint}?custom-quickview=${Date.now()}`);
        })
      );
    } catch (error) {
      console.warn('custom-quickview: Judge.me widgets could not be mounted', error);
    } finally {
      // The module scans synchronously as it evaluates, so by here it is done.
      mountedElsewhere.forEach((node) => {
        node.dataset.entryPoint = node.dataset.customQuickviewEntry;
        delete node.dataset.customQuickviewEntry;
      });
    }
  }

  static judgeMeManifest(baseUrl) {
    if (!CustomQuickview.manifestPromise) {
      CustomQuickview.manifestPromise = fetch(`${baseUrl}manifest.json`)
        .then((response) => (response.ok ? response.json() : {}))
        .catch(() => ({}));
    }
    return CustomQuickview.manifestPromise;
  }

  /** Mirrors the loader's manifest walk: an entry's own CSS plus its imports'. */
  static loadJudgeMeStyles(baseUrl, manifest, entryKey, seen = new Set()) {
    const entry = manifest?.[entryKey];
    if (!entry || seen.has(entryKey)) return;
    seen.add(entryKey);

    (entry.css || []).forEach((href) => {
      const url = `${baseUrl}${href}`;
      if (document.querySelector(`link[href="${url}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      document.head.appendChild(link);
    });

    (entry.imports || []).forEach((key) => CustomQuickview.loadJudgeMeStyles(baseUrl, manifest, key, seen));
  }

  /* --------------------------------------------------------------- drag */

  get isDesktop() {
    return this.desktopQuery?.matches ?? false;
  }

  bindDragHandlers() {
    this.unbindDragHandlers();
    if (!this.grabber || this.isDesktop || this.reducedMotionQuery?.matches) return;

    this.dragAbortController = new AbortController();
    const options = { signal: this.dragAbortController.signal };

    this.grabber.addEventListener('pointerdown', this.onPointerDown, options);
    this.grabber.addEventListener('pointermove', this.onPointerMove, options);
    this.grabber.addEventListener('pointerup', this.onPointerUp, options);
    this.grabber.addEventListener('pointercancel', this.onPointerUp, options);
  }

  unbindDragHandlers() {
    this.dragAbortController?.abort();
    this.dragAbortController = null;
    this.drag = null;
  }

  onPointerDown = (event) => {
    if (this.isDesktop) return;
    this.drag = {
      id: event.pointerId,
      startY: event.clientY,
      offset: 0,
      lastY: event.clientY,
      lastTime: performance.now(),
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    this.panel.classList.add('custom-quickview__panel--dragging');
  };

  onPointerMove = (event) => {
    if (!this.drag || event.pointerId !== this.drag.id) return;

    const delta = event.clientY - this.drag.startY;
    // Rubber-band upward drags so the sheet resists over-travel.
    this.drag.offset = delta > 0 ? delta : delta * 0.25;
    this.drag.lastY = event.clientY;
    this.drag.lastTime = performance.now();
    this.setDragOffset(this.drag.offset);
  };

  onPointerUp = (event) => {
    if (!this.drag) return;

    const height = this.panel.getBoundingClientRect().height;
    const elapsed = Math.max(1, performance.now() - this.drag.lastTime);
    const velocity = (event.clientY - this.drag.lastY) / elapsed;
    const offset = this.drag.offset;

    this.panel.classList.remove('custom-quickview__panel--dragging');
    this.drag = null;

    const shouldClose =
      offset > height * CustomQuickview.DRAG_DISMISS_RATIO || velocity > CustomQuickview.DRAG_DISMISS_VELOCITY;

    if (shouldClose) this.hide();
    else this.setDragOffset(0);
  };

  setDragOffset(px) {
    this.panel?.style.setProperty('--custom-quickview-drag', `${px}px`);
  }

  /* --------------------------------------------------------------- close */

  /** Loading affordance only — aria-expanded tracks the overlay, not the fetch. */
  setOpenerBusy(isBusy) {
    const opener = this.openedBy;
    if (!opener) return;

    if (isBusy) opener.setAttribute('aria-disabled', 'true');
    else opener.removeAttribute('aria-disabled');
    opener.classList.toggle('loading', isBusy);
    opener.querySelector('.loading__spinner')?.classList.toggle('hidden', !isBusy);
  }

  hide({ returnFocus = true } = {}) {
    if (!this.hasAttribute('open')) return;

    this.fetchAbortController?.abort();
    this.removeAttribute('open');
    this.removeAttribute('data-loading');
    this.setDragOffset(0);
    this.unbindDragHandlers();
    this.pendingSubmit = false;
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(returnFocus ? this.openedBy : null);
    pauseAllMedia();
    this.cleanupOrphans();

    if (this.openedBy) {
      this.openedBy.setAttribute('aria-expanded', 'false');
      this.openedBy = null;
    }

    // Clear after the exit transition so the panel does not visibly empty.
    this.clearContentTimer = setTimeout(() => {
      this.contentEl.innerHTML = '';
      if (this.statusEl) this.statusEl.textContent = '';
    }, 400);
  }

  /** Sweep anything that teleported to <body> out of the injected subtree. */
  cleanupOrphans() {
    document
      .querySelectorAll('body > custom-size-guide-drawer[data-section-id^="quickadd-"], body > modal-dialog[id*="quickadd-"]')
      .forEach((node) => node.remove());
  }
}

if (!customElements.get('custom-quickview')) {
  customElements.define('custom-quickview', CustomQuickview);
}

/* ==========================================================================
   Mega menu
   Enhances snippets/custom-mega-menu.liquid. Each item is still a Dawn
   <header-menu>, which owns click toggling, focus-out closing and closing
   when the sticky header hides; this adds hover intent, one open panel at a
   time, positioning each panel under its trigger, Escape and outside-click
   closing, image warm-up and theme editor block selection.
   ========================================================================== */
class CustomMegaMenu extends HTMLElement {
  static OPEN_DELAY = 80; // ms before a hovered item opens when none is open
  static CLOSE_DELAY = 200; // ms grace period after the pointer leaves

  connectedCallback() {
    if (this.abortController) return;

    this.items = Array.from(this.querySelectorAll('[data-custom-mega-menu-item]'));
    if (!this.items.length) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.items.forEach((item) => {
      item.addEventListener('pointerenter', (event) => this.onPointerEnter(event, item), { signal });
      item.addEventListener('pointerleave', (event) => this.onPointerLeave(event, item), { signal });
      item.addEventListener('toggle', () => this.onToggle(item), { signal });
    });

    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('pointerenter', () => this.warmImages(), { signal, once: true });
    this.addEventListener('focusin', () => this.warmImages(), { signal, once: true });

    window.addEventListener('resize', () => this.openItem && this.position(this.openItem), { signal });
    document.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
    document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
    document.addEventListener('shopify:block:deselect', (event) => this.onBlockDeselect(event), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.clearTimers();
  }

  get openItem() {
    return this.items.find((item) => item.open);
  }

  onPointerEnter(event, item) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);
    if (item.open) return;

    // Moving straight from one open panel to the next item should feel instant.
    const delay = this.openItem ? 0 : CustomMegaMenu.OPEN_DELAY;
    this.openTimer = setTimeout(() => {
      this.open(item);
      this.hoverOpenedItem = item;
    }, delay);
  }

  onPointerLeave(event, item) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.openTimer);
    if (!item.open || item === this.editorPinnedItem) return;

    this.closeTimer = setTimeout(() => this.close(item), CustomMegaMenu.CLOSE_DELAY);
  }

  onToggle(item) {
    const summary = item.querySelector('summary');

    if (item.open) {
      this.items.forEach((other) => {
        if (other !== item && other.open) this.close(other);
      });
      summary?.setAttribute('aria-expanded', 'true');
      this.position(item);
      this.warmImages();
    } else {
      summary?.setAttribute('aria-expanded', 'false');
      if (this.hoverOpenedItem === item) this.hoverOpenedItem = null;
    }
  }

  onClick(event) {
    const summary = event.target.closest('summary');
    const item = summary?.parentElement;
    if (!item || !this.items.includes(item)) return;

    // A mouse click on a panel that hover just opened would otherwise toggle
    // it shut. Keyboard activation (detail 0) and touch keep native toggling.
    if (event.detail > 0 && item.open && item === this.hoverOpenedItem) {
      event.preventDefault();
      // global.js flips aria-expanded on every summary click; restore it.
      summary.setAttribute('aria-expanded', 'true');
    }
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;
    const item = this.openItem;
    if (!item) return;

    const focusWasInside = item.contains(document.activeElement);
    this.close(item);
    if (focusWasInside) item.querySelector('summary')?.focus();
  }

  onDocumentClick(event) {
    const item = this.openItem;
    if (item && !item.contains(event.target) && item !== this.editorPinnedItem) this.close(item);
  }

  onBlockSelect(event) {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const block = this.querySelector(`[data-custom-mega-menu-block="${CSS.escape(blockId)}"]`);
    const item = block?.closest('[data-custom-mega-menu-item]');
    if (!item) return;

    this.editorPinnedItem = item;
    this.open(item);
  }

  onBlockDeselect(event) {
    const item = this.editorPinnedItem;
    if (!item) return;
    if (!item.querySelector(`[data-custom-mega-menu-block="${CSS.escape(event.detail?.blockId || '')}"]`)) return;

    this.editorPinnedItem = null;
    this.close(item);
  }

  open(item) {
    if (item.open) return;
    item.open = true; // fires `toggle`, which closes the others and syncs ARIA
    this.position(item); // before paint, so the panel never jumps sideways
  }

  /* Shift the panel so it starts under its trigger, clamped inside the
     header's content box, and bridge the header padding between the trigger
     and the panel. Without both, the pointer leaves the item on its way to
     the panel and hover closes it. */
  position(item) {
    const summary = item.querySelector('summary');
    const dropdown = item.querySelector('.custom-mega-menu__dropdown');
    const panel = dropdown?.querySelector('.custom-mega-menu__panel');
    const header = this.closest('.header');
    if (!summary || !dropdown || !panel || !header) return;

    panel.style.translate = '';
    const summaryRect = summary.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const headerStyle = getComputedStyle(header);
    const minLeft = headerRect.left + parseFloat(headerStyle.paddingLeft);
    const maxLeft = headerRect.right - parseFloat(headerStyle.paddingRight) - panelRect.width;
    const left = Math.max(minLeft, Math.min(summaryRect.left, maxLeft));

    panel.style.translate = `${Math.round(left - panelRect.left)}px 0`;
    item.style.setProperty(
      '--custom-mega-menu-bridge',
      `${Math.max(0, Math.ceil(dropdown.getBoundingClientRect().top - summaryRect.bottom))}px`
    );
  }

  close(item) {
    const headerMenu = item.closest('header-menu');
    if (headerMenu && typeof headerMenu.close === 'function') {
      headerMenu.close();
    } else {
      item.open = false;
    }
  }

  /* Closed <details> content is not rendered, so lazy promo images would only
     start loading once a panel opens. Start them when the menu is first
     approached instead, so panels open with their images ready. */
  warmImages() {
    if (this.imagesWarmed) return;
    this.imagesWarmed = true;
    this.querySelectorAll('[data-custom-mega-menu-media] img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });
  }

  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }
}

if (!customElements.get('custom-mega-menu')) {
  customElements.define('custom-mega-menu', CustomMegaMenu);
}

/* ==========================================================================
   Sticky Add to cart
   Enhances snippets/custom-sticky-atc.liquid. Moves the bar to <body> so it
   pins to the viewport, shows it once the section's own Add to cart has
   scrolled up out of view, and hands its clicks to that button so
   product-form.js still does the adding. Swaps in the re-rendered bar from the
   section HTML that product-info.js publishes on variant change.
   ========================================================================== */
class CustomStickyAtc extends HTMLElement {
  static BUSY_TIMEOUT = 8000; // ms before a stuck spinner gives up

  connectedCallback() {
    // A product swap re-renders the whole section, bar included, so drop any
    // bar the previous render left on <body>.
    if (this.parentElement !== document.body) {
      document.querySelectorAll(`body > #${CSS.escape(this.id)}`).forEach((old) => old.remove());
      document.body.appendChild(this);
      return;
    }

    if (this.abortController) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', (event) => this.onClick(event), { signal });

    const form = document.getElementById(this.dataset.productFormId);
    this.target = form?.closest('product-form') || form;
    if (this.target) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.toggle(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      });
      this.observer.observe(this.target);
    }

    if (typeof subscribe === 'function') {
      this.unsubscribers = [
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => this.onVariantChange(data)),
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.setBusy(false)),
        subscribe(PUB_SUB_EVENTS.cartError, () => this.setBusy(false)),
      ];
    }

    this.resizeObserver = new ResizeObserver(() => this.updateHeight());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    if (!this.abortController) return;
    this.abortController.abort();
    this.abortController = null;
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
    if (!document.body.querySelector('custom-sticky-atc.is-visible')) {
      document.body.classList.remove('custom-sticky-atc-open', 'custom-sticky-atc-open--desktop');
    }
  }

  toggle(visible) {
    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', String(!visible));
    document.body.classList.toggle('custom-sticky-atc-open', visible);
    document.body.classList.toggle(
      'custom-sticky-atc-open--desktop',
      visible && this.classList.contains('custom-sticky-atc--desktop')
    );
    if (visible) this.updateHeight();
  }

  updateHeight() {
    document.body.style.setProperty('--custom-sticky-atc-height', `${this.offsetHeight}px`);
  }

  onClick(event) {
    const button = event.target.closest('[data-custom-sticky-atc-action]');
    if (!button || button.disabled) return;

    if (button.dataset.customStickyAtcAction === 'choose') {
      this.scrollToPicker();
      return;
    }

    const addButton = document.getElementById(this.dataset.productFormId)?.querySelector('[name="add"]');
    if (!addButton || addButton.disabled || addButton.getAttribute('aria-disabled') === 'true') {
      this.scrollToPicker();
      return;
    }

    this.setBusy(true);
    addButton.click();
  }

  scrollToPicker() {
    const picker =
      document.querySelector(`#ProductInfo-${this.dataset.sectionId} variant-selects`) || this.target;
    if (!picker) return;
    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    picker.querySelector('input:not([disabled]), select')?.focus({ preventScroll: true });
  }

  setBusy(busy) {
    clearTimeout(this.busyTimer);
    const button = this.querySelector('[data-custom-sticky-atc-action="add"]');
    if (!button) return;
    button.classList.toggle('loading', busy);
    button.setAttribute('aria-busy', String(busy));
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !busy);
    if (busy) this.busyTimer = setTimeout(() => this.setBusy(false), CustomStickyAtc.BUSY_TIMEOUT);
  }

  onVariantChange(data) {
    if (data.sectionId !== this.dataset.sectionId) return;
    const next = data.html.getElementById(this.id);
    if (!next) return;
    this.querySelector('.custom-sticky-atc__inner').replaceWith(next.querySelector('.custom-sticky-atc__inner'));
  }
}

if (!customElements.get('custom-sticky-atc')) {
  customElements.define('custom-sticky-atc', CustomStickyAtc);
}

/* ==========================================================================
   Colour swatches
   Enhances snippets/custom-colour-swatches.liquid. Each swatch is a link to a
   sibling product. Inside the product page's <product-info>, a click is handed
   to product-info.js the same way a combined-listing option is: it fetches the
   sibling and swaps the page content in place, updating the URL and title, so
   the header, cart drawer and scroll position are untouched. The sibling's
   option value ids for the shopper's current selections (e.g. size) ride along
   so the swap lands on the same size in one request. Anywhere else (or with a
   modifier key) the link just navigates.
   ========================================================================== */
class CustomColourSwatches extends HTMLElement {
  static SWAP_TIMEOUT = 8000; // ms before giving up on the in-place swap

  connectedCallback() {
    if (this.abortController) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.nameTarget = this.querySelector('[data-custom-colour-swatches-name]');

    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('pointerover', (event) => this.previewName(event.target), { signal });
    this.addEventListener('focusin', (event) => this.previewName(event.target), { signal });
    this.addEventListener('pointerleave', () => this.showName(this.dataset.selectedName), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(event.relatedTarget)) this.showName(this.dataset.selectedName);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    clearTimeout(this.fallbackTimer);
  }

  onClick(event) {
    const swatch = event.target.closest('[data-custom-colour-swatch]');
    if (!swatch || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const productInfo = this.closest('product-info');
    if (!productInfo) return;

    // The quick view renders its own template, which product-info.js can't
    // re-request by section id, so the overlay swaps its own content instead.
    const quickview = this.closest('custom-quickview');
    if (quickview) {
      if (typeof quickview.swapProduct !== 'function') return;
      event.preventDefault();
      if (swatch.getAttribute('aria-current') === 'true' || swatch.getAttribute('aria-busy') === 'true') return;

      this.select(swatch);
      quickview.swapProduct(swatch.href, this.matchSelectedOptionValues(swatch, productInfo));
      return;
    }

    if (typeof publish !== 'function') return;

    event.preventDefault();
    if (swatch.getAttribute('aria-current') === 'true' || swatch.getAttribute('aria-busy') === 'true') return;

    this.select(swatch);
    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event,
        target: swatch,
        selectedOptionValues: this.matchSelectedOptionValues(swatch, productInfo),
      },
    });
  }

  // Optimistic: shows the new selection while product-info.js fetches, then
  // its swap replaces this element with the server-rendered one.
  // If the swap never lands (network error), fall back to a normal page load
  // rather than leaving the shopper on a half-selected swatch.
  select(swatch) {
    this.querySelectorAll('[data-custom-colour-swatch]').forEach((element) => {
      const isSelected = element === swatch;
      element.toggleAttribute('aria-busy', isSelected);
      if (isSelected) element.setAttribute('aria-current', 'true');
      else element.removeAttribute('aria-current');
    });
    this.dataset.selectedName = swatch.dataset.colourName || '';
    this.showName(this.dataset.selectedName);

    clearTimeout(this.fallbackTimer);
    this.fallbackTimer = setTimeout(() => window.location.assign(swatch.href), CustomColourSwatches.SWAP_TIMEOUT);
  }

  matchSelectedOptionValues(swatch, productInfo) {
    let valueIds = {};
    try {
      valueIds = JSON.parse(swatch.dataset.customOptionValueIds || '{}');
    } catch (error) {
      return [];
    }

    const selected = productInfo.querySelectorAll(
      'variant-selects select option[selected], variant-selects fieldset input:checked'
    );
    return Array.from(selected)
      .map(({ value }) => valueIds[value.toLowerCase()])
      .filter(Boolean)
      .map(String);
  }

  previewName(target) {
    const swatch = target.closest?.('[data-custom-colour-swatch]');
    if (swatch) this.showName(swatch.dataset.colourName);
  }

  showName(name) {
    if (this.nameTarget && name) this.nameTarget.textContent = name;
  }
}

if (!customElements.get('custom-colour-swatches')) {
  customElements.define('custom-colour-swatches', CustomColourSwatches);
}

/* ==========================================================================
   Breakpoint video
   Wraps the video section's separate desktop and mobile autoplay videos.
   They render without autoplay and with preload="none", so this plays only
   the copy visible at the current breakpoint and the hidden one never downloads.
   ========================================================================== */

class CustomBreakpointVideo extends HTMLElement {
  static DESKTOP_QUERY = '(min-width: 750px)';

  connectedCallback() {
    this.mediaQuery = window.matchMedia(CustomBreakpointVideo.DESKTOP_QUERY);
    this.abortController = new AbortController();
    this.mediaQuery.addEventListener('change', () => this.sync(), { signal: this.abortController.signal });
    this.sync();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  sync() {
    this.querySelectorAll('video').forEach((video) => {
      // No client rects means a display: none ancestor (the breakpoint-hidden copy).
      if (video.getClientRects().length > 0) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }
}

if (!customElements.get('custom-breakpoint-video')) {
  customElements.define('custom-breakpoint-video', CustomBreakpointVideo);
}
